/* eslint-disable @typescript-eslint/no-explicit-any */
import { sanitize } from 'dompurify';
import { escape, find, isArray, mergeWith, remove, uniq, uniqWith } from 'lodash';
const collator = typeof Intl !== 'undefined' ? new Intl.Collator('en', { numeric: true }) : undefined;
export function getGlobalScope() {
    return typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : null;
}
export function dictToArray(dict) {
    return Object.values(dict);
}
/**
 * Whether we are in a web browser
 */
export function isWebEnvironment() {
    return getGlobalScope() !== null;
}
/**
 * @returns true if WebCrypto is available
 */
export function isWebCryptoAvailable() {
    return ((isWebEnvironment() && !isReactNativeEnvironment() && !(document && document.documentMode)) ||
        (/Edge/.test(navigator.userAgent) && window.crypto && !!window.crypto.subtle));
}
/**
 * Whether we are in React Native app
 */
export function isReactNativeEnvironment() {
    return typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
}
/**
 * Searches array of objects for first object where object[key] === value
 * @returns Matching object or null if not found
 */
export function findInArray(array, key, value) {
    return array.find((item) => item[key] === value);
}
/**
 * Searches array of objects for first object where object[key] === value
 * @returns Matching object or null if not found
 */
export function searchArray(array, predicate) {
    return find(array, predicate);
}
export function sureSearchArray(array, predicate) {
    return searchArray(array, predicate);
}
/**
 * @returns Whether the value is a function or object
 */
export function isObject(value) {
    if (value === null) {
        return false;
    }
    return typeof value === 'function' || typeof value === 'object';
}
/**
 * @returns Whether the value is a function
 */
export function isFunction(value) {
    if (value === null) {
        return false;
    }
    return typeof value === 'function';
}
/**
 * @returns True if the object is null or undefined, otherwise false
 */
export function isNullOrUndefined(value) {
    return value === null || value === undefined;
}
export function isNotUndefined(val) {
    return val != undefined;
}
/**
 * @returns True if the string is empty or undefined
 */
export function isEmpty(string) {
    return !string || string.length === 0;
}
/**
 * @returns Whether the value is a string
 */
export function isString(value) {
    return typeof value === 'string' || value instanceof String;
}
/**
 * @returns The greater of the two dates
 */
export function greaterOfTwoDates(dateA, dateB) {
    if (dateA > dateB) {
        return dateA;
    }
    else {
        return dateB;
    }
}
/**
 * Returns a new array containing only unique values by combining the two input arrays.
 * Elements are unique based on the values of `equalityKeys`.
 * @param equalityKeys - Keys to determine element equality
 * @returns Array containing unique values
 */
export function uniqCombineObjArrays(arrayA, arrayB, equalityKeys) {
    return uniqWith(arrayA.concat(arrayB), (a, b) => {
        for (const key of equalityKeys) {
            if (a[key] !== b[key]) {
                return false;
            }
        }
        return true;
    });
}
/**
 * Returns a new array containing only unique values
 * @returns Array containing unique values
 */
export function uniqueArray(array) {
    return uniq(array);
}
/**
 * Returns a new array containing only unique values
 * @returns Array containing unique values
 */
export function uniqueArrayByKey(array, key) {
    return uniqWith(array, (a, b) => {
        return a[key] === b[key];
    });
}
/**
 * Returns the last element in the array.
 * @returns The last element in the array
 */
export function lastElement(array) {
    return array[array.length - 1];
}
/**
 * Adds all items from otherArray into inArray, in-place.
 * Does not return a value.
 */
export function extendArray(inArray, otherArray) {
    for (const value of otherArray) {
        inArray.push(value);
    }
}
/**
 * Removes all items appearing in toSubtract from inArray, in-place
 * @param toSubtract - The list of items to remove from inArray
 */
export function subtractFromArray(inArray, toSubtract) {
    for (const value of toSubtract) {
        removeFromArray(inArray, value);
    }
}
/**
 * Removes the first matching element of an array by strict equality.
 * If no matchin element is found, the array is left unchanged.
 */
export function removeFromArray(array, value) {
    const valueIndex = array.indexOf(value);
    if (valueIndex === -1) {
        return;
    }
    array.splice(valueIndex, 1);
}
/**
 * Adds the element to the array if the array does not already include the value.
 * The array is searched via array.indexOf
 * @returns true if value was added
 */
export function addIfUnique(array, value) {
    if (!array.includes(value)) {
        array.push(value);
        return true;
    }
    return false;
}
/**
 * Removes an object from the array in-place by searching for an object where all the
 * key/values in predicate match with the candidate element.
 */
export function filterFromArray(array, predicate) {
    remove(array, predicate);
}
/**
 * Returns a new array by removing all elements in subtract from array
 */
export function arrayByDifference(array, subtract) {
    return array.filter((x) => !subtract.includes(x)).concat(subtract.filter((x) => !array.includes(x)));
}
export function compareArrayReferences(arr1, arr2) {
    return arr1.length === arr2.length && arr1.every((val, index) => val === arr2[index]);
}
export function compareValues(left, right) {
    if ((left && !right) || (!left && right)) {
        return false;
    }
    if (left instanceof Date && right instanceof Date) {
        return left.getTime() === right.getTime();
    }
    else if (left instanceof String && right instanceof String) {
        return left === right;
    }
    else {
        return topLevelCompare(left, right);
    }
}
/**
 * Removes the value from the array at the given index, in-place.
 */
export function removeFromIndex(array, index) {
    array.splice(index, 1);
}
/**
 * Adds the value from the array at the given index, in-place.
 */
export function addAtIndex(array, element, index) {
    array.splice(index, 0, element);
}
/**
 * Returns a new array by removeing the value from the array at the given index
 */
export function arrayByRemovingFromIndex(array, index) {
    const copy = array.slice();
    removeFromIndex(copy, index);
    return copy;
}
/**
 * Returns an array where each element is the value of a top-level
 * object key.
 * Example: objectToValueArray({a: 1, b: 2}) returns [1, 2]
 */
export function objectToValueArray(object) {
    const values = [];
    for (const key of Object.keys(object)) {
        values.push(object[key]);
    }
    return values;
}
/**
 * Returns a key-sorted copy of the object.
 * For example, sortedCopy({b: '1', a: '2'}) returns {a: '2', b: '1'}
 */
export function sortedCopy(object) {
    const keys = Object.keys(object).sort();
    const result = {};
    for (const key of keys) {
        result[key] = object[key];
    }
    return Copy(result);
}
export const sortByKey = (input, key) => {
    const compare = (a, b) => {
        const valueA = a[key];
        const valueB = b[key];
        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }
        return 0;
    };
    const newArray = [...input];
    newArray.sort(compare);
    return newArray;
};
/** Returns a new object by omitting any keys which have an undefined or null value  */
export function omitUndefinedCopy(object) {
    const result = {};
    for (const key of Object.keys(object)) {
        if (!isNullOrUndefined(object[key])) {
            result[key] = object[key];
        }
    }
    return result;
}
/**
 * Returns a new array by sorting an array of elements based on a date property,
 * as indicated by the input key value.
 */
export function dateSorted(elements, key, ascending = true) {
    return elements.sort((a, b) => {
        const aTimestamp = a[key].getTime();
        const bTimestamp = b[key].getTime();
        const vector = ascending ? 1 : -1;
        if (aTimestamp < bTimestamp) {
            return -1 * vector;
        }
        else if (aTimestamp > bTimestamp) {
            return 1 * vector;
        }
        else {
            return 0;
        }
    });
}
/** Compares for equality by comparing top-level keys value equality (===) */
export function topLevelCompare(left, right) {
    if (!left && !right) {
        return true;
    }
    if (!left || !right) {
        return false;
    }
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) {
        return false;
    }
    for (const key of leftKeys) {
        if (left[key] !== right[key]) {
            return false;
        }
    }
    return true;
}
/**
 * Returns a new object by attempting to JSON.parse any top-level object keys.
 */
export function jsonParseEmbeddedKeys(object) {
    const result = {};
    for (const key of Object.keys(object)) {
        let value;
        try {
            value = JSON.parse(object[key]);
        }
        catch {
            value = object[key];
        }
        result[key] = value;
    }
    return result;
}
export const withoutLastElement = (array) => {
    return array.slice(0, -1);
};
/**
 * Deletes keys of the input object.
 */
export function omitInPlace(object, keys) {
    if (!object) {
        return;
    }
    for (const key of keys) {
        delete object[key];
    }
}
/**
 * Creates a new object by omitting `keys` from `object`
 */
export function omitByCopy(object, keys) {
    if (isNullOrUndefined(object)) {
        return undefined;
    }
    const newObject = Object.assign({}, object);
    /**
     * Lodash's omit, which was previously used, seems to cause unexpected behavior
     * when payload is an ES6 item class. So we instead manually omit each key.
     */
    for (const key of keys) {
        delete newObject[key];
    }
    return newObject;
}
/**
 * Similiar to Node's path.join, this function combines an array of paths into
 * one resolved path.
 */
export function joinPaths(...args) {
    return args
        .map((part, i) => {
        if (i === 0) {
            return part.trim().replace(/[/]*$/g, '');
        }
        else {
            return part.trim().replace(/(^[/]*|[/]*$)/g, '');
        }
    })
        .filter((x) => x.length)
        .join('/');
}
/**
 * Creates a copy of the input object by JSON stringifying the object then JSON parsing
 * the string (if the input is an object). If input is date, a Date copy will be created,
 * and if input is a primitive value, it will be returned as-is.
 */
export function Copy(object) {
    if (object instanceof Date) {
        return new Date(object);
    }
    else if (isObject(object)) {
        return JSON.parse(JSON.stringify(object));
    }
    else {
        return object;
    }
}
/**
 * Merges the second object parameter into the first object, in-place.
 * @returns The now modified first object parameter passed into the function.
 */
export function deepMerge(a, b) {
    /**
     * lodash.merge will not merge a full array with an empty one.
     * deepMerge will replace arrays wholesale
     */
    if (!a || !b) {
        throw 'Attempting to deepMerge with null values';
    }
    const customizer = (aValue, bValue) => {
        if (isArray(aValue)) {
            return bValue;
        }
    };
    mergeWith(a, b, customizer);
    return a;
}
/**
 * Returns a new object by selecting certain keys from input object.
 */
export function pickByCopy(object, keys) {
    const result = {};
    for (const key of keys) {
        result[key] = object[key];
    }
    return Copy(result);
}
/**
 * Recursively makes an object immutable via Object.freeze
 */
export function deepFreeze(object) {
    const propNames = Object.getOwnPropertyNames(object);
    for (const name of propNames) {
        const value = object[name];
        if (value && typeof value === 'object' && !Object.isFrozen(value)) {
            object[name] = deepFreeze(value);
        }
        else {
            object[name] = value;
        }
    }
    return Object.freeze(object);
}
export function isValidUrl(url) {
    try {
        new URL(url);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Determines if an object has a getter defined for a given property
 */
export function hasGetter(object, property) {
    const descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(object), property);
    return descriptor && !isNullOrUndefined(descriptor.get);
}
/**
 * Truncates a hex string into a desired number of bits
 * @returns A hexadecimal string truncated to the number of desired bits
 */
export function truncateHexString(string, desiredBits) {
    const BITS_PER_HEX_CHAR = 4;
    const desiredCharLength = desiredBits / BITS_PER_HEX_CHAR;
    return string.substring(0, desiredCharLength);
}
/**
 * When awaited, this function allows code execution to pause for a set time.
 * Should be used primarily for testing.
 */
export async function sleep(milliseconds, warn = true, desc = '') {
    if (warn) {
        console.warn(`Sleeping for ${milliseconds}ms ${desc}`);
    }
    return new Promise((resolve) => {
        setTimeout(function () {
            resolve();
        }, milliseconds);
    });
}
export function assertUnreachable(uncheckedCase) {
    throw Error('Unchecked case ' + uncheckedCase);
}
/**
 * Returns a boolean representing whether two dates are on the same day
 */
export function isSameDay(dateA, dateB) {
    return (dateA.getFullYear() === dateB.getFullYear() &&
        dateA.getMonth() === dateB.getMonth() &&
        dateA.getDate() === dateB.getDate());
}
/**
 * Sorts an array of objects in natural order
 * @param items - The array of objects to sort
 * @param property - The objects' property to sort by
 * @param direction - The sorting direction, either ascending (default) or descending
 * @returns Array of objects sorted in natural order
 */
export function naturalSort(items, property, direction = 'asc') {
    switch (direction) {
        case 'asc':
            return [...items].sort(collator
                ? (a, b) => collator.compare(a[property], b[property])
                : (a, b) => a[property].localeCompare(b[property], 'en', { numeric: true }));
        case 'desc':
            return [...items].sort(collator
                ? (a, b) => collator.compare(b[property], a[property])
                : (a, b) => b[property].localeCompare(a[property], 'en', { numeric: true }));
    }
}
export function arraysEqual(left, right) {
    if (left.length !== right.length) {
        return false;
    }
    return left.every((item) => right.includes(item)) && right.every((item) => left.includes(item));
}
const MicrosecondsInAMillisecond = 1000;
const MillisecondsInASecond = 1000;
var TimestampDigits;
(function (TimestampDigits) {
    TimestampDigits[TimestampDigits["Seconds"] = 10] = "Seconds";
    TimestampDigits[TimestampDigits["Milliseconds"] = 13] = "Milliseconds";
    TimestampDigits[TimestampDigits["Microseconds"] = 16] = "Microseconds";
})(TimestampDigits || (TimestampDigits = {}));
export function convertTimestampToMilliseconds(timestamp) {
    const digits = String(timestamp).length;
    switch (digits) {
        case TimestampDigits.Seconds:
            return timestamp * MillisecondsInASecond;
        case TimestampDigits.Milliseconds:
            return timestamp;
        case TimestampDigits.Microseconds:
            return Math.floor(timestamp / MicrosecondsInAMillisecond);
        default:
            throw `Unhandled timestamp precision: ${timestamp}`;
    }
}
export function sanitizeHtmlString(html) {
    return sanitize(html);
}
export function escapeHtmlString(html) {
    return escape(html);
}
const itemDateFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
};
let sharedItemDateFormattingLocale;
export function setSharedItemDateFormattingLocale(locale) {
    sharedItemDateFormattingLocale = locale;
}
function getBrowserDateFormattingLocale() {
    if (typeof navigator === 'undefined') {
        return 'en-US';
    }
    if (navigator.languages && navigator.languages.length > 0) {
        return navigator.languages[0];
    }
    return navigator.language || 'en-US';
}
function resolveItemDateFormattingLocale(explicitLocale) {
    if (explicitLocale) {
        return explicitLocale;
    }
    if (sharedItemDateFormattingLocale) {
        return sharedItemDateFormattingLocale;
    }
    return getBrowserDateFormattingLocale();
}
const itemDateFormatters = new Map();
function getItemDateFormatter(locale) {
    let formatter = itemDateFormatters.get(locale);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, itemDateFormatOptions);
        itemDateFormatters.set(locale, formatter);
    }
    return formatter;
}
export function dateToLocalizedString(date, locale) {
    if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
        return getItemDateFormatter(resolveItemDateFormattingLocale(locale)).format(date);
    }
    // IE < 11, Safari <= 9.0.
    return date.toDateString() + ' ' + date.toLocaleTimeString();
}
export function nonSecureRandomIdentifier() {
    return `${Math.random() * 100}`.replace('.', '');
}
export function splitString(string, parts) {
    const outputLength = string.length;
    const partLength = outputLength / parts;
    const partitions = [];
    for (let i = 0; i < parts; i++) {
        const partition = string.slice(partLength * i, partLength * (i + 1));
        partitions.push(partition);
    }
    return partitions;
}
export function firstHalfOfString(string) {
    return string.substring(0, string.length / 2);
}
export function secondHalfOfString(string) {
    return string.substring(string.length / 2, string.length);
}
export function log(namespace, ...args) {
    logWithColor(namespace, 'black', ...args);
}
export function logWithColor(namespace, namespaceColor, ...args) {
    const date = new Date();
    const timeString = `${date.toLocaleTimeString().replace(' PM', '').replace(' AM', '')}.${date.getMilliseconds()}`;
    customLog(`%c${namespace}%c${timeString}`, `color: ${namespaceColor}; font-weight: bold; margin-right: 4px`, 'color: gray', ...args);
}
function customLog(..._args) {
    // eslint-disable-next-line no-console, prefer-rest-params
    Function.prototype.apply.call(console.log, console, arguments);
}
export function assert(value) {
    if (value === undefined) {
        throw new Error('Assertion failed; value must be defined');
    }
}
export function useBoolean(value, defaultValue) {
    return value != undefined ? value : defaultValue;
}
export function spaceSeparatedStrings(...strings) {
    return strings.join(' ');
}
export function pluralize(count, singular, plural) {
    return count === 1 ? singular : plural;
}
export function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            if (reader.result && typeof reader.result === 'string') {
                resolve(reader.result);
            }
            else {
                reject();
            }
        };
        reader.readAsDataURL(blob);
    });
}
